import { theme } from "@tm/utility/global";
import React, { useCallback, useMemo } from "react";

import FormattedText, {
  getPlainText,
} from "../../../../components/FormattedText/FormattedText";
import { Card, CardPadding } from "../../Claims.style";
import { useClaimAction } from "../../modalManager";
import type { ClaimedCard, ClaimStatusCardProps } from "../../types";
import {
  ActionButton,
  CardList,
  ClaimedCardContent,
  ClaimedCardTitle,
  ClaimedCardWrapper,
  ClaimTypeTitle,
  GradientBar,
  GradientBarContainer,
  GradientBarTrack,
  NoClaimsText,
  SectionTitle,
  TitleRow,
} from "./ClaimStatusCard.style";

function resolveGradientColors(raw: string | string[] | undefined): string[] {
  if (Array.isArray(raw) && raw.length >= 2) return raw;
  if (typeof raw === "string") return [raw, raw];
  return [theme.lightBlack, theme.lightBlack];
}

function GradientBottomBar({ colors }: { colors: string[] }) {
  return (
    <GradientBarContainer>
      <GradientBarTrack />
      <GradientBar $colors={colors} />
    </GradientBarContainer>
  );
}

function ClaimedCardItem({ card }: { card: ClaimedCard }) {
  const { style, title, claimTypeTitle, action } = card;
  const runClaimAction = useClaimAction();

  const gradientColors = useMemo(
    () => resolveGradientColors(style?.bottomBorderColor),
    [style?.bottomBorderColor]
  );

  const actionTitle = getPlainText(action?.title).trim();

  const onActionPress = useCallback(() => {
    if (action?.actionId) {
      runClaimAction(action.actionId, { action, card });
    }
  }, [action, card, runClaimAction]);

  return (
    <ClaimedCardWrapper>
      <ClaimedCardContent>
        <TitleRow>
          {getPlainText(title).trim() ? (
            <ClaimedCardTitle $color={style?.titleColor}>
              <FormattedText value={title} />
            </ClaimedCardTitle>
          ) : null}

          {actionTitle ? (
            <ActionButton onClick={onActionPress} aria-label={actionTitle}>
              <FormattedText value={action?.title} />
            </ActionButton>
          ) : null}
        </TitleRow>

        {getPlainText(claimTypeTitle).trim() ? (
          <ClaimTypeTitle $color={style?.claimTypeTitleColor}>
            <FormattedText value={claimTypeTitle} />
          </ClaimTypeTitle>
        ) : null}
      </ClaimedCardContent>

      <GradientBottomBar colors={gradientColors} />
    </ClaimedCardWrapper>
  );
}

export const ClaimStatusCard: React.FC<ClaimStatusCardProps> = ({
  title,
  noClaimsTitle,
  claimedCards,
}) => {
  const hasClaims = Array.isArray(claimedCards) && claimedCards.length > 0;

  return (
    <Card>
      <CardPadding>
        {getPlainText(title).trim() ? (
          <SectionTitle>
            <FormattedText value={title} />
          </SectionTitle>
        ) : null}

        {!hasClaims && getPlainText(noClaimsTitle).trim() ? (
          <NoClaimsText>
            <FormattedText value={noClaimsTitle} />
          </NoClaimsText>
        ) : null}

        {hasClaims ? (
          <CardList>
            {claimedCards?.map((card, index) => (
              <ClaimedCardItem key={card.state ?? index} card={card} />
            ))}
          </CardList>
        ) : null}
      </CardPadding>
    </Card>
  );
};
